import React, { useState } from 'react';
import { contactAPI } from '../config/api';
import useAnimeReveal from '../hooks/useAnimeReveal';
import useAnimePageLoad from '../hooks/useAnimePageLoad';
import { showAppToast } from '../utils/notifications';
import { trackContactSubmission } from '../utils/analytics';
import {
  buildQuoteMessage,
  isDuplicateQuoteRequest,
  rememberQuoteRequest,
} from '../utils/quoteRequests';
import keysHero from '../assets/AppImages/keys.jpg';
import callCenterIcon from '../assets/AppImages/call-center.svg';
import ceoImage from '../assets/AppImages/home_page/ceo2Crop.jpg';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  city: '',
  country: '',
  phone: '',
  message: '',
};

const countries = [
  'Zimbabwe',
  'South Africa',
  'United Arab Emirates',
  'Australia',
  'Melbourne',
  'Angola',
  'United Kingdom',
];

const officeHours = [
  { day: 'Mon - Fri', time: '08:00 - 17:00' },
  { day: 'Saturday', time: '08:00 - 13:00' },
  { day: 'Sunday', time: 'Closed', closed: true },
];

const branchOffices = [
  {
    region: 'Africa',
    locations: [
      ['Zimbabwe -', 'Harare, Bulawayo'],
      ['South Africa -', 'Johannesburg'],
      ['Angola -', 'Luanda'],
    ],
  },
  {
    region: 'Europe',
    locations: [['United Kingdom -', 'London']],
  },
  {
    region: 'Middle East',
    locations: [['United Arab Emirates -', 'Dubai']],
  },
  {
    region: 'Australia',
    locations: [['Melbourne', '']],
  },
];

const mapSrc = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14235.753719474422!2d31.041371909903418!3d-17.895257864334262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a3f22ee38a99%3A0x757348b8f2bdbc19!2sDeRoyalty%20Car%20Rental!5e0!3m2!1sen!2szw!4v1781128963161!5m2!1sen!2szw';

function ContactUs({ requestType = 'contact' }) {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const isQuoteRequest = requestType === 'quote';

  const rootRef = useAnimePageLoad({ selector: '[data-anime-enter]' });
  useAnimeReveal({ selector: '[data-anime]', rootRef });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);

    const basePayload = {
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
      email: formData.email,
      city: formData.city,
      country: formData.country,
      phone: formData.phone,
      message: formData.message,
    };
    const payload = isQuoteRequest
      ? {
          ...basePayload,
          message: buildQuoteMessage({
            message: basePayload.message,
            requestSource: 'Website quotation request',
          }),
        }
      : basePayload;

    if (isQuoteRequest && isDuplicateQuoteRequest(payload)) {
      showAppToast({
        message: 'A matching quote request was already sent from this device. Our team will respond as quickly as possible.',
        type: 'info',
        duration: 3500,
      });
      setLoading(false);
      return;
    }

    try {
      await contactAPI.submit(payload);
      if (isQuoteRequest) rememberQuoteRequest(payload);
      trackContactSubmission(requestType);

      showAppToast({
        message: isQuoteRequest
          ? 'Quote request sent! We will respond with the quotation shortly.'
          : 'Message has been sent! We will respond to you shortly',
        type: 'success',
      });

      setFormData(initialFormData);
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Message failed to send. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="reference-contact-page" ref={rootRef} id="home">
      <section
        className="contact-hero"
        style={{ backgroundImage: `url(${keysHero})` }}
        aria-label="Car keys handoff"
      >
        <div className="contact-hero-copy" data-anime-enter>
          <p className="deroyalty-eyebrow">{isQuoteRequest ? 'Quote DeRoyalty' : 'Contact DeRoyalty'}</p>
          <h1>{isQuoteRequest ? 'Request a tailored rental quote.' : 'Ready when your journey needs us.'}</h1>
          <p>
            {isQuoteRequest
              ? 'Share the journey, billing contact, vehicle preference, and timing so our team can prepare the right quotation.'
              : 'Reach our reservations desk, branch team or executive office for fast, clear support before, during and after your rental.'}
          </p>
          <a href="#contact-form" className="contact-hero-action">
            {isQuoteRequest ? 'Start a Quote' : 'Start a Message'}
            <i className="bi bi-arrow-right" aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="reference-contact-card contact-form-card" data-anime aria-labelledby="contact-form">
        <div className="contact-card-heading">
          <h1 id="contact-form">{isQuoteRequest ? 'Request a Quote' : 'Contact Us'}</h1>
          <p className="contact-lede">
            {isQuoteRequest
              ? 'Tell us who should receive the quotation and which journey details matter most.'
              : 'Do you want to send us a message?'}
          </p>
        </div>

        <p className="contact-intro">
          {isQuoteRequest
            ? 'Share the traveller, company, route, preferred vehicle, dates, and any billing requirements. To prevent duplicate requests, matching quote submissions from this device are held for review before another identical request is accepted.'
            : "If you can't find an answer to your questions, please don't hesitate to contact us. In order for us to give you the best possible help please make sure that you get in touch with the correct person."}
        </p>

        <form className="contact-form-grid" onSubmit={handleSubmit}>
          <label className="contact-field" htmlFor="firstName">
            <span>First name</span>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              required
            />
          </label>

          <label className="contact-field" htmlFor="lastName">
            <span>Last name</span>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              required
            />
          </label>

          <label className="contact-field" htmlFor="email">
            <span>Email</span>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="johnd@gmail.com"
              required
            />
          </label>

          <label className="contact-field" htmlFor="city">
            <span>City</span>
            <input
              id="city"
              name="city"
              type="text"
              value={formData.city}
              onChange={handleChange}
              placeholder="Harare"
              required
            />
          </label>

          <label className="contact-field" htmlFor="country">
            <span>Country</span>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Choose...</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </label>

          <label className="contact-field" htmlFor="phone">
            <span>Phone</span>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+263782223334"
              required
            />
          </label>

          <label className="contact-field contact-field-wide" htmlFor="message">
            <span>{isQuoteRequest ? 'Quote details' : 'Your Message'}</span>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              placeholder={isQuoteRequest ? 'Vehicle type, rental dates, pickup/drop-off locations, company or personal billing details...' : 'Enter Message'}
              required
            />
          </label>

          <div className="contact-submit-row">
            <button type="submit" className="contact-submit-button" disabled={loading}>
              {loading ? 'Sending...' : isQuoteRequest ? 'Send Quote Request' : 'Send Message'}
            </button>
          </div>
        </form>
      </section>

      <section className="contact-touch-section" data-anime aria-labelledby="get-in-touch-title">
        <h2 id="get-in-touch-title">Get in touch with us</h2>
        <p>Want to get in touch with us?</p>

        <div className="contact-card-grid" data-anime-stagger="true">
          <article className="contact-info-card">
            <div className="contact-info-media">
              <img src={callCenterIcon} alt="call center" />
            </div>
            <div className="contact-info-body">
              <h3>Contact Center</h3>
              <p>Email:<br /><a href="mailto:customercare@deroyaltycarrental.com">customercare@deroyaltycarrental.com</a></p>
              <p>Phone:<br /><a href="tel:+263242004351">+263 24 2004351</a></p>
              <p>Address:<br />2870 Mainway Meadows<br />Waterfalls, Harare</p>
              <p>(available 24/7, sometimes you may be required to wait)</p>
            </div>
            <a href="#contact-form" className="contact-call-button">Call now</a>
          </article>

          <article className="contact-info-card">
            <div className="contact-info-media">
              <img src={ceoImage} alt="ceo" className="contact-ceo-image" />
            </div>
            <div className="contact-info-body">
              <h3>Ceo Office</h3>
              <p>Email:<br /><a href="mailto:brianlangsoni@deroyaltycarrental.com">brianlangsoni@deroyaltycarrental.com</a></p>
              <p>Phone:<br /><a href="tel:+263712768037">+263712768037</a></p>
              <p>(available 24/7, sometimes you may be required to wait)</p>
            </div>
            <a href="#contact-form" className="contact-call-button">Call now</a>
          </article>
        </div>
      </section>

      <section className="office-hours-card" data-anime aria-labelledby="office-hours-title">
        <h2 id="office-hours-title">Office Hours</h2>
        {officeHours.map((item) => (
          <div className="hours-row" key={item.day}>
            <span className="hours-day">{item.day}</span>
            <span className="hours-separator" />
            <span className={`hours-time ${item.closed ? 'closed' : ''}`}>{item.time}</span>
          </div>
        ))}
      </section>

      <section className="branch-offices-section" data-anime aria-labelledby="branch-offices-title">
        <div className="branch-heading">
          <h2 id="branch-offices-title">Branch Offices</h2>
          <p>Please look at the list of branch offices in Africa, Europe, Australia and the Middle East</p>
        </div>

        <div className="branch-grid" data-anime-stagger="true">
          {branchOffices.map((branch) => (
            <div className="branch-region" key={branch.region}>
              <h3>{branch.region}</h3>
              <ul>
                {branch.locations.map(([country, city]) => (
                  <li key={`${branch.region}-${country}`}>
                    <strong>{country}</strong>{city ? ` ${city}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="visit-section" data-anime aria-labelledby="visit-title">
        <div className="visit-copy">
          <h2 id="visit-title">Visit us</h2>
          <p className="visit-support-line">
            Our Harare branch is ready for pickups, returns and in-person support.
          </p>
        </div>

        <address className="visit-contact-card">
          <span>Branch office</span>
          <strong>2870 Mainway Meadows</strong>
          <span>Waterfalls, Harare</span>
          <a href="tel:+263712768037">+263712768037</a>
        </address>
      </section>

      <section className="contact-map-section" data-anime data-anime-direction="scale" aria-label="DeRoyalty location map">
        <iframe
          src={mapSrc}
          title="DeRoyalty Car Rental map"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </main>
  );
}

export default ContactUs;
